
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  aggregateGuideFeedbackStats,
  getGuideVoteStats,
  getVisitorVote,
  loadGuideFeedbackState,
  normalizeXatUsername,
  recordGuideDislikeWithMessage,
  recordGuideLike,
  saveGuideFeedbackState,
  type GuideFeedbackState,
  type GuideFeedbackSummary,
  type GuideNegativeFeedback,
  type GuideVoteStats,
} from '@/lib/guide-feedback';
import * as guideFeedbackService from '@/services/guide-feedback.service';

type GuideFeedbackContextValue = {
  hydrated: boolean;
  summary: GuideFeedbackSummary;
  getStats: (guideKey: string) => GuideVoteStats;
  getVisitorVote: (guideKey: string) => 'like' | 'dislike' | null;
  submitLike: (guideKey: string) => void;
  submitNegativeFeedback: (input: {
    guideKey: string;
    categoryId: string;
    slug: string;
    articleTitle: string;
    message: string;
    xatUsername?: string;
  }) => boolean;
  negativeFeedback: GuideNegativeFeedback[];
  negativeFeedbackForGuide: (guideKey: string) => GuideNegativeFeedback[];
};

const GuideFeedbackContext = createContext<GuideFeedbackContextValue | null>(null);

export function GuideFeedbackProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<GuideFeedbackState>(() => loadGuideFeedbackState());
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    setState(loadGuideFeedbackState());
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    saveGuideFeedbackState(state);
  }, [state, hydrated]);

  const getStats = useCallback((guideKey: string) => getGuideVoteStats(state, guideKey), [state]);

  const getVisitorVoteForGuide = useCallback(
    (guideKey: string) => getVisitorVote(state, guideKey),
    [state]
  );

  const submitLike = useCallback((guideKey: string) => {
    setState((prev) => recordGuideLike(prev, guideKey));
    const [categoryId, slug] = guideKey.split(':');
    if (categoryId && slug) {
      void guideFeedbackService.voteGuide(categoryId, slug, 'like');
    }
  }, []);

  const submitNegativeFeedback = useCallback(
    (input: {
      guideKey: string;
      categoryId: string;
      slug: string;
      articleTitle: string;
      message: string;
      xatUsername?: string;
    }) => {
      const trimmed = input.message.trim();
      if (!trimmed) return false;
      const xatUsername = normalizeXatUsername(input.xatUsername ?? '');
      setState((prev) =>
        recordGuideDislikeWithMessage(prev, {
          ...input,
          xatUsername,
        })
      );
      void guideFeedbackService.submitNegativeFeedback({
        categoryId: input.categoryId,
        slug: input.slug,
        message: trimmed,
        ...(xatUsername ? { xatUsername } : {}),
      });
      return true;
    },
    []
  );

  const negativeFeedbackForGuide = useCallback(
    (guideKey: string) => state.negativeFeedback.filter((f) => f.guideKey === guideKey),
    [state.negativeFeedback]
  );

  const summary = useMemo(() => aggregateGuideFeedbackStats(state), [state]);

  const value = useMemo<GuideFeedbackContextValue>(
    () => ({
      hydrated,
      summary,
      getStats,
      getVisitorVote: getVisitorVoteForGuide,
      submitLike,
      submitNegativeFeedback,
      negativeFeedback: state.negativeFeedback,
      negativeFeedbackForGuide,
    }),
    [
      hydrated,
      summary,
      getStats,
      getVisitorVoteForGuide,
      submitLike,
      submitNegativeFeedback,
      state.negativeFeedback,
      negativeFeedbackForGuide,
    ]
  );

  return <GuideFeedbackContext.Provider value={value}>{children}</GuideFeedbackContext.Provider>;
}

export function useGuideFeedback() {
  const ctx = useContext(GuideFeedbackContext);
  if (!ctx) throw new Error('useGuideFeedback must be used within GuideFeedbackProvider');
  return ctx;
}
